import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowUp, Facebook, Twitter, Linkedin, Instagram, Mail, Phone, MapPin } from 'lucide-react';

const Footer = ({ companyInfo }) => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setMessage({ type: 'error', text: 'Please enter your email address' });
      return;
    }

    setIsSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      const API_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:5001/api' : '/api';
      const response = await axios.post(`${API_URL}/newsletter/subscribe`, {
        email
      });

      if (response.data.success) {
        setMessage({ type: 'success', text: response.data.message });
        setEmail('');
        setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to subscribe. Please try again.';
      setMessage({ type: 'error', text: errorMessage });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const footerLinks = {
    services: [
      { name: 'ERP Systems', href: '#services' },
      { name: 'AI/ML Solutions', href: '#services' },
      { name: 'Cybersecurity', href: '#services' },
      { name: 'Digital Marketing', href: '#services' },
      { name: 'Corporate Training', href: '#services' },
    ],
    company: [
      { name: 'About Us', href: '#about' },
      { name: 'Our Team', href: '#team' },
      { name: 'Portfolio', href: '#portfolio' },
      { name: 'Process', href: '#process' },
      { name: 'Contact', href: '#contact' },
    ],
    resources: [
      { name: 'Blog', href: '/blog' },
      { name: 'Case Studies', href: '#portfolio' },
      { name: 'Support', href: '#contact' },
    ]
  };

  const rawSocialMedia = companyInfo?.socialMedia;
  const socialMedia = typeof rawSocialMedia === 'string'
    ? (() => { try { return JSON.parse(rawSocialMedia); } catch (e) { return {}; } })()
    : rawSocialMedia || {};

  const socialLinks = [
    { icon: Facebook, href: socialMedia.facebook, label: 'Facebook' },
    { icon: Twitter, href: socialMedia.twitter, label: 'Twitter' },
    { icon: Linkedin, href: socialMedia.linkedin, label: 'LinkedIn' },
    { icon: Instagram, href: socialMedia.instagram, label: 'Instagram' },
  ].filter(social => social.href);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' }
    }
  };

  return (
    <footer className="bg-lion-blue text-white">
      {/* Back to Top Button */}
      <button
        onClick={scrollToTop}
        className="fixed bottom-20 right-6 bg-lion-yellow text-lion-blue p-3 rounded-full shadow-lg hover:bg-yellow-400 transform hover:scale-110 transition-all duration-300 z-50"
        aria-label="Back to top"
      >
        <ArrowUp className="w-6 h-6" />
      </button>

      <div className="container mx-auto px-6 py-16">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {/* Company Info */}
          <motion.div variants={itemVariants} className="lg:col-span-1">
            <div className="mb-6">
              <h3 className="text-2xl font-poppins font-bold text-lion-yellow mb-4">
                {companyInfo?.companyName || 'LionTek'}
              </h3>
              <p className="text-gray-300 leading-relaxed mb-6">
                {companyInfo?.description || 'Empowering businesses through innovative technology solutions. We transform ideas into reality with cutting-edge development and strategic innovation.'}
              </p>
            </div>

            {/* Contact Info */}
            <div className="space-y-3">
              <div className="flex items-center">
                <MapPin className="w-5 h-5 text-lion-yellow mr-3 flex-shrink-0" />
                <span className="text-sm text-gray-300">{companyInfo?.address || '123 Innovation Drive, Tech City, TC 12345'}</span>
              </div>
              <div className="flex items-center">
                <Phone className="w-5 h-5 text-lion-yellow mr-3 flex-shrink-0" />
                <span className="text-sm text-gray-300">{companyInfo?.phone || '+1 (555) 123-4567'}</span>
              </div>
              <div className="flex items-center">
                <Mail className="w-5 h-5 text-lion-yellow mr-3 flex-shrink-0" />
                <span className="text-sm text-gray-300">{companyInfo?.email || 'hello@liontek.com'}</span>
              </div>
            </div>
          </motion.div>

          {/* Services */}
          <motion.div variants={itemVariants}>
            <h4 className="text-lg font-poppins font-semibold mb-6">Services</h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-gray-300 hover:text-lion-yellow transition-colors duration-300 text-sm"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Company */}
          <motion.div variants={itemVariants}>
            <h4 className="text-lg font-poppins font-semibold mb-6">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="text-gray-300 hover:text-lion-yellow transition-colors duration-300 text-sm"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Resources */}
          <motion.div variants={itemVariants}>
            <h4 className="text-lg font-poppins font-semibold mb-6">Resources</h4>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.name}>
                  {link.href.startsWith('/') ? (
                    <Link
                      to={link.href}
                      className="text-gray-300 hover:text-lion-yellow transition-colors duration-300 text-sm"
                    >
                      {link.name}
                    </Link>
                  ) : (
                    <a
                      href={link.href}
                      className="text-gray-300 hover:text-lion-yellow transition-colors duration-300 text-sm"
                    >
                      {link.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </motion.div>
        </motion.div>

        {/* Newsletter Signup */}
        <motion.div
          variants={itemVariants}
          className="border-t border-gray-700 pt-8 mt-12"
        >
          <div className="md:flex md:items-center md:justify-between">
            <div className="mb-6 md:mb-0">
              <h4 className="text-lg font-poppins font-semibold mb-2">Stay Updated</h4>
              <p className="text-gray-300 text-sm">
                Subscribe to our newsletter for the latest tech insights and company updates.
              </p>
            </div>
            
            <div className="flex-1 md:max-w-md">
              <form onSubmit={handleNewsletterSubmit} className="flex">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 bg-white text-gray-900 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-lion-yellow disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <button 
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-lion-yellow text-lion-blue px-6 py-2 rounded-r-lg font-semibold hover:bg-yellow-400 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Subscribing...' : 'Subscribe'}
                </button>
              </form>
              {message.text && (
                <p className={`text-sm mt-2 ${message.type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                  {message.text}
                </p>
              )}
            </div>
          </div>
        </motion.div>

        {/* Bottom Bar */}
        <motion.div
          variants={itemVariants}
          className="border-t border-gray-700 pt-8 mt-8"
        >
          <div className="md:flex md:items-center md:justify-between">
            <div className="mb-6 md:mb-0">
              <p className="text-gray-300 text-sm">
                © {currentYear} {companyInfo?.companyName || 'LionTek'}. All rights reserved.
              </p>
            </div>
            
            {/* Social Links */}
            <div className="flex space-x-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-700 rounded-full flex items-center justify-center text-gray-300 hover:bg-lion-yellow hover:text-lion-blue transition-all duration-300"
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Additional Legal Links */}
        <div className="border-t border-gray-700 pt-6 mt-6">
          <div className="flex flex-wrap justify-center md:justify-start space-x-6 text-sm text-gray-400">
            <Link to="/terms-of-service" className="hover:text-lion-yellow transition-colors duration-300">
              Terms of Service
            </Link>
            <Link to="/privacy-policy" className="hover:text-lion-yellow transition-colors duration-300">
              Privacy Policy
            </Link>
            <Link to="/cookie-policy" className="hover:text-lion-yellow transition-colors duration-300">
              Cookie Policy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;